import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import {
  Box,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Measure-uploads (basic_measure_uploads) widget settings. Minimal object
// under one value key, the communication pattern:
//   { cta: boolean, counts: boolean, list: boolean }
// - `cta`   : on/off for the top-right "goto uploads" button. On by default.
// - `counts`: the uploads summary counts line. On by default.
// - `list`  : the newest uploads list. On by default.
// The body rows are data, not config — no per-row visibility settings and
// no order setting. Three widget-level switches in one Paper block.

export type BasicMeasureUploadsSettingsValue = {
  cta?: boolean;
  counts?: boolean;
  list?: boolean;
};

type Props = {
  value: BasicMeasureUploadsSettingsValue;
  onChange: (v: BasicMeasureUploadsSettingsValue) => void;
  // Unused for this widget (no order setting). Kept in the shared
  // SettingsProps shape so the dialog renders it uniformly.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.basic_measure_uploads';

export const CoachSettingsWidgetBasicMeasureUploads: FunctionComponent<Props> = ({
  value,
  onChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Defensive defaults so the dialog works before the first save.
  const cta = HelpersClientWidgets.getBasicMeasureUploadsCtaEnabled(value);
  const counts = HelpersClientWidgets.getBasicMeasureUploadsCountsEnabled(value);
  const list = HelpersClientWidgets.getBasicMeasureUploadsListEnabled(value);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Widget-level toggles. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.basicMeasureUploadsCountsIcon
            sx={{fontSize: '1.2rem', color: counts ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.counts`)}
            value={counts}
            greying
            onChange={(c) => onChange({ ...value, counts: c })}
          />
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.basicMeasureUploadsListIcon
            sx={{fontSize: '1.2rem', color: list ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.list`)}
            value={list}
            greying
            onChange={(c) => onChange({ ...value, list: c })}
          />
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.basicMeasureUploadsCtaIcon
            sx={{fontSize: '1.2rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.cta`)}
            value={cta}
            greying
            onChange={(c) => onChange({ ...value, cta: c })}
          />
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

// Read-only status: the three toggle icons coloured by on/off so the placed
// box summarises the config at a glance.
type StatusProps = {
  value: BasicMeasureUploadsSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetBasicMeasureUploadsStatus: FunctionComponent<StatusProps> = ({
  value
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const cta = HelpersClientWidgets.getBasicMeasureUploadsCtaEnabled(value);
  const counts = HelpersClientWidgets.getBasicMeasureUploadsCountsEnabled(value);
  const list = HelpersClientWidgets.getBasicMeasureUploadsListEnabled(value);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={t(`${LABEL_BASE}.options.counts`)}>
        <HelpersClientWidgets.basicMeasureUploadsCountsIcon
          sx={{ fontSize: '1.1rem', color: counts ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
      <Box title={t(`${LABEL_BASE}.options.list`)}>
        <HelpersClientWidgets.basicMeasureUploadsListIcon
          sx={{ fontSize: '1.1rem', color: list ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
      <Box title={t(`${LABEL_BASE}.options.cta`)}>
        <HelpersClientWidgets.basicMeasureUploadsCtaIcon
          sx={{ fontSize: '1.1rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
    </Box>
  </Box>;
}
